import React, { useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useForm } from 'react-hook-form';
import { useQuery } from 'react-query';
import { useAuth } from '../contexts/AuthContext';
import { FiMail, FiLock } from 'react-icons/fi';
import LoadingSpinner from '../components/LoadingSpinner';
import { api } from '../utils/api';
import { renderMarkdown } from '../utils/markdown';

const Login = () => {
  const [isLoading, setIsLoading] = useState(false);
  const { login } = useAuth();
  const navigate = useNavigate();
  
  const {
    register,
    handleSubmit,
    formState: { errors },
  } = useForm();

  const onSubmit = async (data) => {
    setIsLoading(true);
    const result = await login(data.email, data.password);
    setIsLoading(false);
    
    if (result.success) {
      navigate('/');
    }
  };

  const { data: notes = [] } = useQuery(
    'publicNotes',
    async () => {
      const response = await api.get('/notes/public?limit=9');
      return response.data.notes || [];
    }
  );

  return (
    <div className="min-h-screen flex flex-col items-center justify-center bg-gray-50 py-12 px-4 sm:px-6 lg:px-8">
      <div className="max-w-md w-full space-y-8">
        <div>
          <div className="mx-auto h-12 w-12 flex items-center justify-center rounded-full bg-primary-100">
            <img src="/futurenote-icon.svg" alt="FutureNote" className="h-10 w-10" />
          </div>
          <h2 className="mt-6 text-center text-3xl font-extrabold text-gray-900">
            Sign in to FutureNote
          </h2>
          <p className="mt-2 text-center text-sm text-gray-600">
            Or{' '}
            <Link
              to="/register"
              className="font-medium text-primary-600 hover:text-primary-500"
            >
              create a new account
            </Link>
          </p>
          <div className="mt-4 rounded-md border border-amber-100 bg-amber-50 px-3 py-2 text-center text-xs text-amber-700">
            FutureNote is under active development. Data may be lost and some features may not work yet.
          </div>
        </div>

        <form className="mt-8 space-y-6" onSubmit={handleSubmit(onSubmit)}>
          <div className="space-y-4">
            <div>
              <label htmlFor="email" className="block text-sm font-medium text-gray-700">
                Email address
              </label>
              <div className="mt-1 relative">
                <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                  <FiMail className="h-5 w-5 text-gray-400" />
                </div>
                <input
                  id="email"
                  type="email"
                  autoComplete="email"
                  className="input-field pl-10"
                  placeholder="Enter your email"
                  {...register('email', {
                    required: 'Email is required',
                    pattern: {
                      value: /^[A-Z0-9._%+-]+@[A-Z0-9.-]+\.[A-Z]{2,}$/i,
                      message: 'Invalid email address',
                    },
                  })}
                />
              </div>
              {errors.email && (
                <p className="mt-1 text-sm text-red-600">{errors.email.message}</p>
              )}
            </div>

            <div>
              <label htmlFor="password" className="block text-sm font-medium text-gray-700">
                Password
              </label>
              <div className="mt-1 relative">
                <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                  <FiLock className="h-5 w-5 text-gray-400" />
                </div>
                <input
                  id="password"
                  type="password"
                  autoComplete="current-password"
                  className="input-field pl-10"
                  placeholder="Enter your password"
                  {...register('password', {
                    required: 'Password is required',
                    minLength: {
                      value: 6,
                      message: 'Password must be at least 6 characters',
                    },
                  })}
                />
              </div>
              {errors.password && (
                <p className="mt-1 text-sm text-red-600">{errors.password.message}</p>
              )}
            </div>
          </div>

          <div>
            <button
              type="submit"
              disabled={isLoading}
              className="btn-primary w-full flex justify-center items-center"
            >
              {isLoading ? <LoadingSpinner size="sm" /> : 'Sign in'}
            </button>
          </div>

        </form>
      </div>

      {notes.length > 0 && (
        <div className="mt-12 max-w-5xl w-full">
          <h3 className="text-sm font-semibold text-gray-900 mb-3">Latest FutureNotes</h3>
          <div className="columns-1 sm:columns-2 lg:columns-3 gap-4">
            {notes.map((note) => (
              <div key={note.id} className="note-tile mb-4 rounded-lg border border-gray-200 bg-white p-4 shadow-sm">
                <div
                  className="text-sm text-gray-700 space-y-2"
                  dangerouslySetInnerHTML={{ __html: renderMarkdown(note.content) }}
                />
                <div className="mt-3 text-xs text-gray-400">
                  {new Date(note.publishAt).toLocaleString()}
                  {note.author?.firstName && (
                    <> · {note.author.firstName} {note.author.lastName}</>
                  )}
                </div>
              </div>
            ))}
          </div>
        </div>
      )}

      <footer className="mt-10 text-xs text-gray-400">
        Boutique Tech 2026
      </footer>
    </div>
  );
};

export default Login;


